import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { nanoid } from 'nanoid';

import { ReactComponent as TickIcon } from '../../../../assets/images/tickIcon.svg';
import { ReactComponent as TickIconDisabled } from '../../../../assets/images/tickIconDisabled.svg';
import { ReactComponent as UploadIcon } from '../../../../assets/images/upload.svg';
import { ReactComponent as UploadInactiveIcon } from '../../../../assets/images/upload-inactive.svg';
import BackButton from '../../../../components/shared/BackButton/BackButton';
import Button from '../../../../components/shared/Button/Button';
import Checkbox from '../../../../components/shared/Checkbox/Checkbox';
import CustomTextField from '../../../../components/shared/CustomTextField/CustomTextField';
import { LINKEDIN_URL } from '../../../../constants/commonConstants';
import { fetchLinkedinProfile, updateUserProfile } from '../../../../redux/actions/userActions';
import urls from '../../../../routing/urls';

import styles from './LinkedinImport.module.scss';

const LinkedinImport = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const id = useSelector((state) => state.authReducer.currentUser?.user.id);
  const userDetails = useSelector((state) => state.userReducer.user);
  const [aboutData, setAboutData] = useState({});
  const [avatarData, setAvatarData] = useState({});
  const [experiencesData, setExperiencesData] = useState([]);
  const [educationData, setEducationData] = useState([]);
  const [linksData, setLinksData] = useState([]);
  const [url, setUrl] = useState('');
  const [isImported, setIsImported] = useState(false);
  const [isAll, setIsAll] = useState(false);

  const formatLinks = useCallback((contact_info) => {
    const linksArray = [];
    if (contact_info?.websites && Array.isArray(contact_info.websites)) {
      contact_info.websites.forEach((sites, index) => {
        const nanoId = nanoid();
        linksArray.push({
          id: index,
          type: `wb_${nanoId}`,
          value: sites.url,
          isChecked: true,
        });
      });
    }
    return linksArray;
  }, []);

  const formatExperiences = useCallback((experienceGroups) => {
    const groupArray = experienceGroups.map((eachGroup) => eachGroup.profile_positions);
    const formattedGroupArray = [];
    groupArray.forEach((eachGroup) => {
      eachGroup.forEach((group) => {
        formattedGroupArray.push({
          id: group.company + group.title,
          job_title: group.title,
          company: group.company,
          description: group.description,
          location: group.location,
          start_year: group.date.start.year,
          end_year: group.date.end.year,
          current_position: !group.date.end.year,
          isChecked: true,
        });
      });
    });
    return formattedGroupArray;
  }, []);

  const successCallback = useCallback(
    (data) => {
      const linksFormattedData = formatLinks(data.contact_info);
      const experiences = Array.isArray(data.position_groups) ? formatExperiences(data.position_groups) : [];
      const about = {
        bio: data.summary || '',
        isChecked: true,
      };
      const education = Array.isArray(data.education)
        ? data.education.map(({ degree_name, field_of_study, date, school }, index) => ({
            id: -index - 1,
            degree: degree_name,
            field_study: field_of_study,
            start_year: date.start?.year,
            end_year: date.end?.year,
            university: school?.name,
            isChecked: true,
          }))
        : [];
      const avatar = {
        url: data.profile_picture || '',
        isChecked: !!data.profile_picture,
      };
      setIsImported(true);
      setExperiencesData(experiences);
      setEducationData(education);
      setLinksData(linksFormattedData);
      setAboutData(about);
      setAvatarData(avatar);
      setIsAll(true);
    },
    [setExperiencesData, formatLinks, formatExperiences]
  );

  const handleFetchProfile = useCallback(() => {
    dispatch(fetchLinkedinProfile(url.replace('/', '').trim(), successCallback));
  }, [url]);

  const toggleSelectAll = useCallback(
    (e) => {
      const { checked } = e.target;
      if (checked) {
        setExperiencesData(experiencesData.map((experience) => ({ ...experience, isChecked: true })));
        setEducationData(educationData.map((education) => ({ ...education, isChecked: true })));
        setLinksData(linksData.map((link) => ({ ...link, isChecked: true })));
        setAboutData({ ...aboutData, isChecked: true });
        setAvatarData({ ...avatarData, isChecked: true });
        setIsAll(true);
      } else {
        setExperiencesData(experiencesData.map((experience) => ({ ...experience, isChecked: false })));
        setEducationData(educationData.map((education) => ({ ...education, isChecked: false })));
        setLinksData(linksData.map((link) => ({ ...link, isChecked: false })));
        setAboutData({ ...aboutData, isChecked: false });
        setAvatarData({ ...avatarData, isChecked: false });
        return setIsAll(false);
      }
    },
    [experiencesData, educationData, linksData, aboutData]
  );

  const handleArrayToggleCheckbox = useCallback(
    (e, idToUpdate, type) => {
      const { checked } = e.target;
      if (!checked) {
        setIsAll(false);
      }
      if (type === 'experience') {
        setExperiencesData(
          experiencesData.map((experience) =>
            experience.id === idToUpdate ? { ...experience, isChecked: checked } : experience
          )
        );
      }
      if (type === 'education') {
        setEducationData(
          educationData.map((education) =>
            education.id === idToUpdate ? { ...education, isChecked: checked } : education
          )
        );
      }
      if (type === 'link') {
        setLinksData(linksData.map((link) => (link.id === idToUpdate ? { ...link, isChecked: checked } : link)));
      }
      if (type === 'bio') {
        setAboutData({ ...aboutData, isChecked: checked });
      }
      if (type === 'avatar') {
        setAvatarData({ ...avatarData, isChecked: checked });
      }
    },
    [experiencesData, educationData, linksData, aboutData]
  );

  const finishSuccessCallback = useCallback(() => {
    setUrl('');
    setExperiencesData([]);
    setLinksData([]);
    setEducationData([]);
    setAboutData({});
    setIsImported(false);
    navigate(urls.profile);
  }, []);

  const appendLinkedinUrl = useCallback(
    (linksJson, formattedLinksData) => {
      const parsedLinks = JSON.parse(linksJson);
      const filteredLinks = parsedLinks.filter((link) => link.type !== 'ln');
      filteredLinks.push({
        type: 'ln',
        value: LINKEDIN_URL + url,
      });
      const filteredOldLinks = [];
      filteredLinks.forEach((oldItem) => {
        let duplicate = false;
        formattedLinksData.forEach((newItem) => {
          if (oldItem.value === newItem.value) {
            duplicate = true;
          }
        });
        if (!duplicate) {
          filteredOldLinks.push(oldItem);
        }
      });
      const updatedLinks = [...filteredOldLinks, ...formattedLinksData];
      return JSON.stringify(updatedLinks);
    },
    [url]
  );

  const filterDuplicateExperience = useCallback((oldExperiences, newExperiences) => {
    const filteredOldArray = [];
    oldExperiences.forEach((oldItem) => {
      let duplicate = false;
      newExperiences.forEach((newItem) => {
        if (oldItem.job_title === newItem.job_title && oldItem.company === newItem.company) {
          duplicate = true;
        }
      });
      if (!duplicate) {
        filteredOldArray.push(oldItem);
      }
    });
    const filteredOldExperiences = filteredOldArray.map(({ id: experienceId, ...keepAttrs }) => keepAttrs);
    return [...filteredOldExperiences, ...newExperiences];
  }, []);

  const filterDuplicateEducation = useCallback((oldEducations, newEducations) => {
    const filteredOldArray = [];
    oldEducations.forEach((oldItem) => {
      let duplicate = false;
      newEducations.forEach((newItem) => {
        if (
          oldItem.degree === newItem.degree &&
          oldItem.field_study === newItem.field_study &&
          oldItem.university === newItem.university
        ) {
          duplicate = true;
        }
      });
      if (!duplicate) {
        filteredOldArray.push(oldItem);
      }
    });
    const filteredOldEducations = filteredOldArray.map(({ id: educationId, ...keepAttrs }) => keepAttrs);
    return [...filteredOldEducations, ...newEducations];
  }, []);

  const handleFinishImport = useCallback(() => {
    const filteredExperiencesData = experiencesData.filter(({ isChecked }) => isChecked);
    const formattedExperiencesData = filteredExperiencesData.map(
      ({ id: experienceid, isChecked, ...keepAttrs }) => keepAttrs
    );
    const filteredEducationData = educationData.filter(({ isChecked }) => isChecked);
    const formattedEducationData = filteredEducationData.map(
      ({ id: educationId, isChecked, ...keepAttrs }) => keepAttrs
    );
    const filteredLinksData = linksData.filter(({ isChecked }) => isChecked);
    const formattedLinksData = filteredLinksData.map(({ id: linkId, isChecked, ...keepAttrs }) => keepAttrs);
    const formattedData = {
      links_json: appendLinkedinUrl(userDetails.links_json, formattedLinksData),
      user_experiences_attributes: filterDuplicateExperience(userDetails.user_experiences, formattedExperiencesData),
      user_educations_attributes: filterDuplicateEducation(userDetails.user_educations, formattedEducationData),
      is_linkedin_imported: true,
      from_linkedin: true,
    };
    if (avatarData.isChecked && avatarData.url) {
      formattedData.is_linkedin_picture_exists = true;
      formattedData.linkedin_photo_url = avatarData.url;
    }
    if (aboutData.isChecked && aboutData.bio) {
      formattedData.bio = aboutData.bio;
    }
    dispatch(updateUserProfile(id, formattedData, finishSuccessCallback));
  }, [
    dispatch,
    experiencesData,
    educationData,
    id,
    finishSuccessCallback,
    userDetails,
    aboutData,
    linksData,
    avatarData,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.backBtnCont}>
          <BackButton />
        </div>
        <div className={styles.importStepsCont}>
          <p className={styles.heading}>{t('linkedinImport.importFromLinkedin')}</p>
          <div className={styles.divider} />
          <div className={styles.stepCont}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepContent}>
              <p className={styles.stepHeading}>{t('linkedinImport.provideProfileUrl')}</p>
              <div className={styles.urlInputCont}>
                <div className={styles.urlInputFieldCont}>
                  <p className={styles.inputStarter}>{LINKEDIN_URL}</p>
                  <CustomTextField
                    className={styles.urlInput}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder={t('placeholders.username')}
                  />
                </div>
                <div className={styles.uploadBtnCont}>
                  <Button className={styles.uploadBtn} onClick={handleFetchProfile} disabled={!url?.trim()}>
                    {url?.trim() ? <UploadIcon /> : <UploadInactiveIcon />}
                    {t('import')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.divider} />
          <div className={styles.stepContColumn}>
            <div className={styles.stepCont}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <p className={styles.stepHeading}>{t('linkedinImportProfile.saveToProfile')}</p>
                {!isImported && <p className={styles.stepText}>{t('linkedinImportProfile.noUploadYet')}</p>}
              </div>
            </div>
            {isImported && (
              <div className={styles.dataCont}>
                <div className={styles.selectAllCont}>
                  <Checkbox checked={isAll} onChange={toggleSelectAll} />
                  <p className={styles.selectAllText}>{t('selectAll')}</p>
                </div>
                <div className={styles.divider} />
                {!!avatarData.url && (
                  <>
                    <p className={styles.sectionLabel}>{t('linkedinImport.photos')}</p>
                    <div className={`${styles.importedDataCont} align-items-center`}>
                      <Checkbox
                        checked={avatarData.isChecked}
                        onChange={(e) => handleArrayToggleCheckbox(e, null, 'avatar')}
                      />
                      <p className={styles.photoHeading}>{t('linkedinImport.profilePhoto')}</p>
                      <div className={styles.avatarCont}>
                        <img alt="avatar" src={avatarData.url} className={styles.avatar} />
                      </div>
                    </div>
                  </>
                )}
                {!!linksData.length && (
                  <>
                    <p className={styles.sectionLabel}>{t('linkedinImport.links')}</p>
                    {linksData.map((eachLink) => (
                      <div className={styles.importedDataCont} key={eachLink.id}>
                        <Checkbox
                          checked={eachLink.isChecked}
                          onChange={(e) => handleArrayToggleCheckbox(e, eachLink.id, 'link')}
                        />
                        <p className={styles.textNormal}>{eachLink.value}</p>
                      </div>
                    ))}
                  </>
                )}
                {!!aboutData.bio && (
                  <>
                    <p className={styles.sectionLabel}>{t('profile.about')}</p>
                    <div className={styles.importedDataCont}>
                      <Checkbox
                        checked={aboutData.isChecked}
                        onChange={(e) => handleArrayToggleCheckbox(e, null, 'bio')}
                      />
                      <p className={styles.textNormal}>{aboutData.bio}</p>
                    </div>
                  </>
                )}
                {!!experiencesData.length && (
                  <>
                    <p className={styles.sectionLabel}>{t('profile.experience')}</p>
                    {experiencesData.map((experience) => (
                      <div className={styles.importedDataCont} key={experience.id}>
                        <Checkbox
                          checked={experience.isChecked}
                          onChange={(e) => handleArrayToggleCheckbox(e, experience.id, 'experience')}
                        />
                        <div>
                          <p className={styles.experienceTitle}>{experience.job_title}</p>
                          <p className={styles.experienceCompany}>{experience.company}</p>
                          <p className={styles.experienceSpan}>{experience.location}</p>
                          <p className={styles.experienceSpan}>
                            {experience.start_year} - {experience.end_year ?? 'present'}
                          </p>
                          <p className={styles.experienceDescription}>{experience.description}</p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {!!educationData.length && (
                  <>
                    <p className={styles.sectionLabel}>{t('profile.education')}</p>
                    {educationData.map((education) => (
                      <div className={styles.importedDataCont} key={education.id}>
                        <Checkbox
                          checked={education.isChecked}
                          onChange={(e) => handleArrayToggleCheckbox(e, education.id, 'education')}
                        />
                        <div>
                          <p className={styles.educationTitle}>{education.university}</p>
                          <p className={styles.experienceSpan}>{education.degree}</p>
                          <p className={styles.experienceSpan}>{education.field_study}</p>
                          <p className={styles.experienceSpan}>
                            {education.start_year} - {education.end_year ?? 'present'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
            <div className={styles.btnCont}>
              <Button className={styles.uploadBtn} disabled={!isImported} onClick={handleFinishImport}>
                {isImported ? <TickIcon /> : <TickIconDisabled />}
                {t('linkedinImport.finishImporting')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedinImport;
